// The drive's file browser: back/forward history, breadcrumbs, a folders
// sidebar with the drive's capacity, icon and list views (sortable list
// columns), and full keyboard navigation of the files as a listbox:
//   arrows / PageUp / PageDown / Home / End   move the selection
//   Enter / Space / ⌘↓ / double-click         open (folder, or photo in the viewer)
//   Backspace / ⌘↑                            up to the drive
// On touch, a tap opens. The browser has no scroller of its own: it grows
// with its contents and the page scrolls it.
import { DRIVE_ICON, FOLDER_ICON, fmtBytes, fmtDate, h, kindOf, nameEl, thumb } from "./files.js";

const ICONS_SVG = `<svg viewBox="0 0 12 12" aria-hidden="true"><rect width="5" height="5" fill="currentColor"/><rect x="7" width="5" height="5" fill="currentColor"/><rect y="7" width="5" height="5" fill="currentColor"/><rect x="7" y="7" width="5" height="5" fill="currentColor"/></svg>`;
const LIST_SVG = `<svg viewBox="0 0 12 12" aria-hidden="true"><rect y="0.5" width="12" height="2" fill="currentColor"/><rect y="5" width="12" height="2" fill="currentColor"/><rect y="9.5" width="12" height="2" fill="currentColor"/></svg>`;
const capacityLabel = (bytes) => `${Math.round(bytes / 1e9)} GB`;

let instances = 0;

export function createDriveBrowser({ manifest: M, viewer }) {
  const uid = `dbr${++instances}`;
  const coarse = matchMedia("(pointer: coarse)").matches;
  const state = { path: "", view: "icons", sort: "name", dir: 1, sel: 0 };
  const hist = { stack: [""], i: 0 };
  let entries = [];

  const back = h("button", { type: "button", "aria-label": "Back", html: "&lsaquo;", onclick: () => hist.i > 0 && go(hist.stack[--hist.i], false) });
  const fwd = h("button", { type: "button", "aria-label": "Forward", html: "&rsaquo;", onclick: () => hist.i < hist.stack.length - 1 && go(hist.stack[++hist.i], false) });
  const crumbs = h("nav.dbr__crumbs", { "aria-label": "Path" });
  const viewButtons = [["icons", "Icons", ICONS_SVG], ["list", "List", LIST_SVG]].map(([view, label, svg]) =>
    h("button", { type: "button", dataset: { view }, "aria-pressed": "false", onclick: () => { state.view = view; render(); } },
      h("span.dbr__svg", { html: svg }), h("span.dbr__seg-label", { text: label })));
  const driveBtn = h("button.dbr__loc", { type: "button", onclick: () => go("") },
    h("span.dbr__svg", { html: DRIVE_ICON }), h("span", { text: `${M.drive} (${capacityLabel(M.capacity)})` }));
  const folderButtons = M.folders.map((f) =>
    h("button.dbr__loc", { type: "button", dataset: { folder: f.name }, onclick: () => go(f.name) },
      h("span.dbr__svg", { html: FOLDER_ICON }), h("span", { text: f.name }), h("span.dbr__n", { text: f.count })));
  const pane = h("div.dbr__pane", { tabindex: "0", role: "listbox", "aria-label": "Files" });
  const status1 = h("span");
  const status2 = h("span");

  const el = h("div.dbr", {},
    h("div.dbr__toolbar", {},
      h("div.dbr__hist", {}, back, fwd),
      crumbs,
      h("div.dbr__seg", { role: "group", "aria-label": "View" }, viewButtons)),
    h("div.dbr__body", {},
      h("aside.dbr__side", { "aria-label": "Locations" },
        // pinned while the page scrolls through a long folder (see drive.css)
        h("div.dbr__side-in", {},
          h("h3.dbr__h.caps", { text: "Device" }),
          h("div.dbr__drive", {},
            driveBtn,
            h("div.dbr__cap", {},
              h("div.dbr__bar", {}, h("i", { style: `width: ${(M.used / M.capacity * 100).toFixed(3)}%` })),
              h("p", { text: `${fmtBytes(M.used)} of ${capacityLabel(M.capacity)} used` }))),
          h("h3.dbr__h.caps", { text: "Folders" }),
          h("ul.dbr__folders", {}, folderButtons.map((b) => h("li", {}, b))))),
      pane),
    h("div.dbr__status", {}, status1, status2));

  function listing() {
    if (!state.path) {
      return M.folders.map((f) => ({ dir: true, name: f.name, bytes: f.bytes, count: f.count, date: M.byFolder(f.name).at(-1).date }));
    }
    const k = state.sort;
    return M.byFolder(state.path).slice().sort((a, b) =>
      (k === "size" ? a.bytes - b.bytes : k === "date" ? a.date.localeCompare(b.date) : a.name.localeCompare(b.name)) * state.dir);
  }

  function go(path, push = true) {
    if (push && path !== hist.stack[hist.i]) {
      hist.stack = hist.stack.slice(0, hist.i + 1);
      hist.stack.push(path);
      hist.i++;
    }
    const prev = state.path;
    state.path = path;
    // going up selects the folder we came from
    state.sel = !path && prev ? Math.max(0, M.folders.findIndex((f) => f.name === prev)) : 0;
    render();
  }

  function render() {
    entries = listing();
    crumbs.replaceChildren(h("button", { type: "button", onclick: () => go(""), "aria-current": state.path ? null : "page", text: M.drive }));
    if (state.path) {
      crumbs.append(h("span.dbr__sep", { "aria-hidden": "true", text: "/" }), h("button", { type: "button", "aria-current": "page", text: state.path }));
    }
    back.disabled = hist.i === 0;
    fwd.disabled = hist.i >= hist.stack.length - 1;
    for (const b of folderButtons) b.setAttribute("aria-current", String(b.dataset.folder === state.path));
    driveBtn.setAttribute("aria-current", String(!state.path));
    for (const b of viewButtons) b.setAttribute("aria-pressed", String(b.dataset.view === state.view));
    pane.setAttribute("aria-label", `Files in ${state.path || M.drive}`);

    const total = entries.reduce((s, e) => s + e.bytes, 0);
    status1.textContent = `${entries.length} ${state.path ? "items" : "folders"} · ${fmtBytes(total)}`;
    status2.textContent = `${fmtBytes(M.capacity - M.used)} available`;

    pane.replaceChildren(state.view === "icons" ? iconView() : listView());
    sizeIconPlaceholders();
    showTop();
    select(state.sel, false);
  }

  // The icons skip rendering while off screen (content-visibility: auto,
  // css/drive.css), laid out meanwhile at a placeholder height. It must be
  // their real height: an icon whose rendering starts as it nears the
  // viewport would otherwise change size under the visitor, pushing what is
  // below it (as End's selection, scrolled into view, was pushed back out).
  // Every icon in a listing is the same height (one kind of item, one-line
  // names), which in a narrow window follows the column width: measured on
  // the first one, rendered for the purpose, with each listing and whenever
  // the pane changes size (setting the same height again changes nothing).
  function sizeIconPlaceholders() {
    const first = pane.querySelector(".dbr__ic");
    if (!first || !pane.isConnected) return; // (the observer measures once it is)
    // its own height, not its grid row's (stretched to its neighbours')
    Object.assign(first.style, { contentVisibility: "visible", alignSelf: "start" });
    const cs = getComputedStyle(first);
    const content = first.getBoundingClientRect().height - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
    Object.assign(first.style, { contentVisibility: "", alignSelf: "" });
    pane.style.setProperty("--ic-h", `${content}px`);
  }
  new ResizeObserver(sizeIconPlaceholders).observe(pane);

  // A new listing starts at its top: if the page has scrolled down into the
  // old one, bring the browser's top back to its scroll margin.
  function showTop() {
    if (!el.isConnected) return;
    if (el.getBoundingClientRect().top < parseFloat(getComputedStyle(el).scrollMarginTop)) el.scrollIntoView({ block: "start", behavior: "instant" });
  }

  const optId = (i) => `${uid}-f${i}`;

  function iconView() {
    return h("div.dbr__icons", { role: "presentation" }, entries.map((e, i) => {
      let visual;
      if (e.dir) {
        const items = M.byFolder(e.name);
        visual = h("div.dbr__folder", { "aria-hidden": "true" }, h("div.dbr__folder-front", {}, thumb(items[3] ?? items[0]), thumb(items[0])));
      } else {
        visual = thumb(e);
      }
      return h("div.dbr__ic", { role: "option", id: optId(i), "aria-selected": "false", dataset: { i } },
        h("div.dbr__box", {}, visual),
        e.dir ? h("span.dbr__nm", { text: e.name }) : nameEl(e.name),
        e.dir ? h("span.dbr__sub", { text: `${e.count} items` }) : null);
    }));
  }

  function listView() {
    const col = (key, label, cls = "") => h(`button${cls}`, {
      type: "button",
      "aria-sort": state.path ? (state.sort === key ? (state.dir > 0 ? "ascending" : "descending") : "none") : null,
      disabled: !state.path,
      onclick: () => {
        state.dir = state.sort === key ? -state.dir : 1;
        state.sort = key;
        render();
      },
      text: label + (state.path && state.sort === key ? (state.dir > 0 ? " ↑" : " ↓") : ""),
    });
    return h("div.dbr__list", { role: "presentation" },
      h("div.dbr__lh", { role: "presentation" }, col("name", "Name"), col("size", "Size", ".dbr__r"), col("kind", "Kind", ".dbr__opt"), col("date", "Date", ".dbr__opt")),
      entries.map((e, i) => h("div.dbr__row", { role: "option", id: optId(i), "aria-selected": "false", dataset: { i } },
        h("div.dbr__nmc", {}, e.dir ? h("span.dbr__th", { html: FOLDER_ICON }) : h("span.dbr__th", {}, thumb(e)), e.dir ? h("span", { text: e.name }) : nameEl(e.name)),
        h("span.dbr__c.dbr__r", { text: e.dir ? `${e.count} items` : fmtBytes(e.bytes) }),
        h("span.dbr__c.dbr__opt", { text: e.dir ? "Folder" : kindOf(e.name) }),
        h("span.dbr__c.dbr__opt", { text: fmtDate(e.date) }))));
  }

  function select(i, scroll = true) {
    if (!entries.length) return;
    i = Math.max(0, Math.min(entries.length - 1, i));
    pane.querySelector('[aria-selected="true"]')?.setAttribute("aria-selected", "false");
    const opt = pane.querySelector(`#${optId(i)}`);
    opt.setAttribute("aria-selected", "true");
    pane.setAttribute("aria-activedescendant", opt.id);
    state.sel = i;
    if (scroll) opt.scrollIntoView({ block: "nearest", behavior: "instant" });
    const e = entries[i];
    if (!e.dir) status1.textContent = `1 of ${entries.length} selected · ${fmtBytes(e.bytes)} · ${e.w} × ${e.h}`;
  }

  function openSelected() {
    const e = entries[state.sel];
    if (e.dir) {
      go(e.name);
      pane.focus();
      return;
    }
    const folder = state.path;
    viewer.showPhotos(entries, state.sel, {
      onChange: (it) => {
        if (it.folder !== folder || state.path !== folder) return;
        const j = entries.indexOf(it);
        if (j >= 0) select(j);
      },
      returnFocus: pane,
    });
  }

  // icons per row, for vertical arrow keys
  function columns() {
    if (state.view !== "icons") return 1;
    const items = pane.querySelectorAll(".dbr__ic");
    let n = 0;
    for (const it of items) {
      if (it.offsetTop !== items[0].offsetTop) break;
      n++;
    }
    return Math.max(1, n);
  }

  pane.addEventListener("click", (ev) => {
    const opt = ev.target.closest("[role=option]");
    if (!opt) return;
    select(Number(opt.dataset.i), false);
    if (coarse) openSelected();
  });
  pane.addEventListener("dblclick", (ev) => {
    if (ev.target.closest("[role=option]")) openSelected();
  });
  pane.addEventListener("keydown", (ev) => {
    const c = columns();
    const icons = state.view === "icons";
    const moves = { ArrowRight: icons ? 1 : 0, ArrowLeft: icons ? -1 : 0, ArrowDown: c, ArrowUp: -c, PageDown: c * 5, PageUp: -c * 5 };
    if (ev.metaKey && ev.key === "ArrowDown") {
      ev.preventDefault();
      openSelected();
    } else if (ev.metaKey && ev.key === "ArrowUp") {
      ev.preventDefault();
      if (state.path) go("");
    } else if (moves[ev.key]) {
      ev.preventDefault();
      select(state.sel + moves[ev.key]);
    } else if (ev.key === "Home" || ev.key === "End") {
      ev.preventDefault();
      select(ev.key === "Home" ? 0 : entries.length - 1);
    } else if (ev.key === "Enter" || ev.key === " ") {
      ev.preventDefault();
      openSelected();
    } else if (ev.key === "Backspace" && state.path) {
      ev.preventDefault();
      go("");
    }
  });

  render();
  return { el, pane };
}
